import notifee, { AndroidImportance } from '@notifee/react-native';
import * as BackgroundFetch from 'expo-background-fetch';
import * as TaskManager from 'expo-task-manager';
import { Platform } from 'react-native';
import { alarmApi, deviceApi } from '../services/api';
import logger from '../utils/logger';
import { platformStorage } from '../utils/storage';

const BACKGROUND_ALARM_CHECK_TASK = 'BACKGROUND_ALARM_CHECK';
const ALARM_CHECK_INTERVAL = 10 * 60; // 10 minutes in seconds

// Only define task if TaskManager is available (not on web)
if (Platform.OS !== 'web' && TaskManager.isTaskDefined) {
  try {
    // Check if task already defined before defining
    const isAlreadyDefined = TaskManager.isTaskDefined(BACKGROUND_ALARM_CHECK_TASK);
    if (!isAlreadyDefined) {
      TaskManager.defineTask(BACKGROUND_ALARM_CHECK_TASK, async () => {
        try {
          logger.info('🔔 Background alarm check started');

          // Get stored user data
          const userDataStr = await platformStorage.getItem('user_data');
          if (!userDataStr) {
            logger.warn('No user data found, skipping alarm check');
            return BackgroundFetch.BackgroundFetchResult.NoData;
          }

          const userData = JSON.parse(userDataStr);
          const orgId = userData.orgId;

          if (!orgId) {
            return BackgroundFetch.BackgroundFetchResult.NoData;
          }

          // Check for offline devices
          const devices = await deviceApi.getDevices(orgId);
          const fifteenMinutesAgo = new Date(Date.now() - 15 * 60 * 1000);
          const offlineDevices = devices.filter((d: any) => {
            // Primary: lastSeenAt timestamp (most accurate)
            if (d.lastSeenAt) {
              const lastSeen = new Date(
                d.lastSeenAt.endsWith('Z') ? d.lastSeenAt : d.lastSeenAt + 'Z',
              );
              return lastSeen <= fifteenMinutesAgo;
            }
            // Fallback: only explicit 'offline' status (warning = connected with alert, not offline)
            if (d.status !== undefined) return d.status === 'offline';
            // Fallback: isOnline boolean
            if (d.isOnline !== undefined && d.isOnline !== null) return d.isOnline !== true;
            // No data at all = consider offline
            return true;
          });

          // Check for active critical alarms
          const activeAlarms = await alarmApi.getActiveAlarms(orgId);
          const criticalAlarms = activeAlarms.filter((a: any) => a.severity === 'critical');

          // Send notifications if there are issues
          let notificationsSent = 0;

          // ✅ FIX: Check quiet hours before sending background notifications
          const { default: notificationService } = await import('./notificationService');
          const prefs = await notificationService.getPreferences();
          const shouldShow = await notificationService.shouldShowNotification();

          if (shouldShow && prefs.deviceOfflineEnabled && offlineDevices.length > 0) {
            // ID يتغير عند تغيّر مجموعة الأجهزة المنقطعة حتى لا يُبتلع الإشعار القديم
            const offlineIds = offlineDevices
              .map((d: any) => d.id)
              .sort()
              .join(',');
            const offlineNotifId = `bg_offline_${offlineIds.slice(0, 60)}`;
            await notifee.displayNotification({
              id: offlineNotifId,
              title:
                offlineDevices.length === 1
                  ? 'جهاز غير متصل | Device Offline'
                  : `${offlineDevices.length} أجهزة غير متصلة | Devices Offline`,
              body:
                offlineDevices.length === 1
                  ? `${offlineDevices[0].name} غير متصل | is offline`
                  : `تحقق من الأجهزة | Check your devices`,
              data: {
                type: 'device_offline',
                count: String(offlineDevices.length),
                deviceId: offlineDevices[0]?.id ? String(offlineDevices[0].id) : '',
              },
              android: {
                channelId: 'offline',
                pressAction: { id: 'default' },
                importance: AndroidImportance.DEFAULT,
                sound: 'default',
                vibrationPattern: prefs.vibrationEnabled ? [200, 300] : undefined,
                smallIcon: 'ic_notification',
              },
              ios: {
                sound: prefs.soundEnabled ? 'default' : undefined,
              },
            });
            notificationsSent++;
          }

          if (shouldShow && prefs.alarmsEnabled && criticalAlarms.length > 0) {
            // ID يتغير عند تغيّر مجموعة الإنذارات الحرجة
            const alarmIds = criticalAlarms
              .map((a: any) => a.id)
              .sort()
              .join(',');
            const alarmNotifId = `bg_critical_${alarmIds.slice(0, 60)}`;
            await notifee.displayNotification({
              id: alarmNotifId,
              title:
                criticalAlarms.length === 1
                  ? 'تنبيه حرج | Critical Alarm'
                  : `${criticalAlarms.length} تنبيهات حرجة | Critical Alarms`,
              body:
                criticalAlarms.length === 1
                  ? criticalAlarms[0].ruleName || criticalAlarms[0].message || 'تحقق من التنبيهات'
                  : 'تحقق من التنبيهات | Check alarms',
              data: {
                type: 'alarm',
                severity: 'critical',
                count: String(criticalAlarms.length),
                alarmId: criticalAlarms[0]?.id ? String(criticalAlarms[0].id) : '',
              },
              android: {
                channelId: 'alarms',
                pressAction: { id: 'default' },
                importance: AndroidImportance.HIGH,
                sound: 'default',
                vibrationPattern: prefs.vibrationEnabled ? [300, 500, 300, 500] : undefined,
                smallIcon: 'ic_notification',
              },
              ios: {
                sound: prefs.soundEnabled ? 'default' : undefined,
                // ✅ timeSensitive breaks through Focus mode — no Apple approval required
                // critical:true was removed — requires Apple Critical Alerts entitlement
                interruptionLevel: 'timeSensitive',
              },
            });
            notificationsSent++;
          }

          logger.info(`✅ Background check complete. Sent ${notificationsSent} notifications`);

          return notificationsSent > 0
            ? BackgroundFetch.BackgroundFetchResult.NewData
            : BackgroundFetch.BackgroundFetchResult.NoData;
        } catch (error) {
          logger.error('❌ Background alarm check failed:', error);
          return BackgroundFetch.BackgroundFetchResult.Failed;
        }
      });
    }
  } catch (error) {
    logger.warn('Failed to define background task:', error);
  }
}

/**
 * Register background alarm checking
 */
export async function registerBackgroundAlarmCheck() {
  // Skip on web platform
  if (Platform.OS === 'web') {
    logger.info('Background tasks not supported on web');
    return false;
  }

  try {
    // Check background fetch availability
    const status = await BackgroundFetch.getStatusAsync();
    if (
      status === BackgroundFetch.BackgroundFetchStatus.Restricted ||
      status === BackgroundFetch.BackgroundFetchStatus.Denied
    ) {
      logger.warn('Background fetch is restricted or denied');
      return false;
    }

    // Check if task is already registered
    const isRegistered = await TaskManager.isTaskRegisteredAsync(BACKGROUND_ALARM_CHECK_TASK);

    if (isRegistered) {
      logger.info('Background alarm check already registered');
      return true;
    }

    // Check if task is defined
    const isDefined = TaskManager.isTaskDefined(BACKGROUND_ALARM_CHECK_TASK);
    if (!isDefined) {
      logger.warn('Background task not defined, skipping registration');
      return false;
    }

    // Register background fetch
    await BackgroundFetch.registerTaskAsync(BACKGROUND_ALARM_CHECK_TASK, {
      minimumInterval: ALARM_CHECK_INTERVAL, // 10 minutes
      stopOnTerminate: false, // Continue after app termination
      startOnBoot: true, // Start when device boots
    });

    logger.info('✅ Background alarm check registered successfully');
    return true;
  } catch (error: any) {
    // Don't throw error, just log it
    if (error?.message?.includes('not defined')) {
      logger.warn('Background task not available in this environment');
    } else {
      logger.error('❌ Failed to register background alarm check:', error);
    }
    return false;
  }
}

/**
 * Unregister background alarm checking
 */
export async function unregisterBackgroundAlarmCheck() {
  try {
    await BackgroundFetch.unregisterTaskAsync(BACKGROUND_ALARM_CHECK_TASK);
    logger.info('Background alarm check unregistered');
    return true;
  } catch (error) {
    logger.error('Failed to unregister background alarm check:', error);
    return false;
  }
}

/**
 * Check background fetch status
 */
export async function getBackgroundFetchStatus(): Promise<BackgroundFetch.BackgroundFetchStatus | null> {
  try {
    return await BackgroundFetch.getStatusAsync();
  } catch {
    return null;
  }
}

/**
 * Manual check for alarms (can be called from UI)
 */
export async function manualAlarmCheck(orgId: string) {
  try {
    logger.info('🔔 Manual alarm check started');

    // Check for offline devices
    const devices = await deviceApi.getDevices(orgId);
    const fifteenMinutesAgo = new Date(Date.now() - 15 * 60 * 1000);
    const offlineDevices = devices.filter((d: any) => {
      // Primary: lastSeenAt timestamp (most accurate)
      if (d.lastSeenAt) {
        const lastSeen = new Date(d.lastSeenAt.endsWith('Z') ? d.lastSeenAt : d.lastSeenAt + 'Z');
        return lastSeen <= fifteenMinutesAgo;
      }
      // Fallback: only explicit 'offline' status (warning = connected with alert, not offline)
      if (d.status !== undefined) return d.status === 'offline';
      // Fallback: isOnline boolean
      if (d.isOnline !== undefined && d.isOnline !== null) return d.isOnline !== true;
      // No data at all = consider offline
      return true;
    });

    // Check for active critical alarms
    const activeAlarms = await alarmApi.getActiveAlarms(orgId);
    const criticalAlarms = activeAlarms.filter((a: any) => a.severity === 'critical');

    return {
      offlineDevices: offlineDevices.length,
      criticalAlarms: criticalAlarms.length,
      totalIssues: offlineDevices.length + criticalAlarms.length,
      devices: offlineDevices,
      alarms: criticalAlarms,
    };
  } catch (error) {
    logger.error('Manual alarm check failed:', error);
    throw error;
  }
}

/**
 * Silence alarms for a period
 */
export async function silenceAlarmsFor(minutes: number) {
  try {
    const silenceUntil = Date.now() + minutes * 60 * 1000;
    await platformStorage.setItem('alarms_silenced_until', silenceUntil.toString());

    logger.info(`🔕 Alarms silenced for ${minutes} minutes`);
    return true;
  } catch (error) {
    logger.error('Failed to silence alarms:', error);
    return false;
  }
}

/**
 * Check if alarms are currently silenced
 */
export async function areAlarmsSilenced(): Promise<boolean> {
  try {
    const silencedUntilStr = await platformStorage.getItem('alarms_silenced_until');
    if (!silencedUntilStr) return false;

    const silencedUntil = parseInt(silencedUntilStr, 10);
    const isSilenced = Date.now() < silencedUntil;

    if (!isSilenced) {
      // Clear expired silence
      await platformStorage.removeItem('alarms_silenced_until');
    }

    return isSilenced;
  } catch (error) {
    logger.error('Failed to check alarm silence status:', error);
    return false;
  }
}

/**
 * Unsilence alarms immediately
 */
export async function unsilenceAlarms() {
  try {
    await platformStorage.removeItem('alarms_silenced_until');
    logger.info('🔔 Alarms unsilenced');
    return true;
  } catch (error) {
    logger.error('Failed to unsilence alarms:', error);
    return false;
  }
}
